import { useEffect, useSyncExternalStore } from "react";

// Keyed by reason, not one shared flag: opening the cart writes an `open-cart`
// search param, and that route change would otherwise clear the cart's own
// suppression. Hidden while any reason is held.
export type PersonaPickerSuppressionReason =
  | "pending-subscription"
  | "cart-open"
  | "checkout-return";

const suppressions = new Set<PersonaPickerSuppressionReason>();
const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getSnapshot = () => suppressions.size > 0;

export const setPersonaPickerSuppressed = (
  reason: PersonaPickerSuppressionReason,
  suppressed: boolean,
) => {
  if (suppressions.has(reason) === suppressed) {
    return;
  }

  if (suppressed) {
    suppressions.add(reason);
  } else {
    suppressions.delete(reason);
  }

  listeners.forEach((listener) => listener());
};

export const useIsPersonaPickerDisabled = () => {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};

export const usePersonaPickerSuppression = (
  reason: PersonaPickerSuppressionReason,
  suppressed: boolean,
) => {
  useEffect(() => {
    setPersonaPickerSuppressed(reason, suppressed);
    return () => setPersonaPickerSuppressed(reason, false);
  }, [reason, suppressed]);
};
